import React, { useState, useEffect } from 'react';
import { Users, ChevronLeft, ChevronRight, Search, User, Edit3, Ban, CheckCircle, X, Key, Eye, EyeOff } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '../components/api';
import { toast } from 'react-toastify';
import useGlobale from '../components/global';
import Sidebar from '../components/SideBar';
import Header from '../components/Header';
import DemandeurMenu from '../components/DemandeurMenu';

const UtilisateurPage = () => {
    const { user } = useGlobale();
    const [loading, setLoading] = useState(true);
    const [users, setUsers] = useState([]);
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [searchTerm, setSearchTerm] = useState('');
    const [totalCount, setTotalCount] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const [searchTimeout, setSearchTimeout] = useState(null);
    const [selectedUser, setSelectedUser] = useState(null);
    const [showProfileModal, setShowProfileModal] = useState(false);
    const [showSuspendModal, setShowSuspendModal] = useState(false);
    const [userToSuspend, setUserToSuspend] = useState(null);
    const [showPasswordModal, setShowPasswordModal] = useState(false);
    const [selectedUserForPassword, setSelectedUserForPassword] = useState(null);
    const [passwordForm, setPasswordForm] = useState({
        new_password: '',
        confirm_password: ''
    });
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        fetchUsers(currentPage, itemsPerPage, searchTerm);
    }, [currentPage, itemsPerPage]);

    // Debounce search - attendre 400ms après la dernière frappe
    useEffect(() => {
        if (searchTimeout) clearTimeout(searchTimeout);
        const timeout = setTimeout(() => {
            setCurrentPage(1);
            fetchUsers(1, itemsPerPage, searchTerm);
        }, 400);
        setSearchTimeout(timeout);
        return () => clearTimeout(timeout);
    }, [searchTerm]);

    const fetchUsers = async (page = 1, pageSize = 10, search = '') => {
        setLoading(true);
        try {
            const params = `?page=${page}&page_size=${pageSize}${search ? `&search=${encodeURIComponent(search)}` : ''}`;
            const response = await api.get(`utilisateur/users/${params}`);
            setUsers(response.data.results || []);
            setTotalCount(response.data.count || 0);
            setTotalPages(response.data.total_pages || 1);
        } catch (error) {
            console.error('Erreur lors de la récupération des utilisateurs:', error);
            toast.error('Erreur lors de la récupération des utilisateurs');
        } finally {
            setLoading(false);
        }
    };

    // Les données viennent déjà paginées et filtrées du serveur
    const currentItems = users;
    const indexOfFirstItem = (currentPage - 1) * itemsPerPage;
    const indexOfLastItem = Math.min(currentPage * itemsPerPage, totalCount);

    const handleToggleStatus = async (userId, currentStatus) => {
        try {
            await api.put(`utilisateur/users/${userId}/toggle-status/`, {
                is_active: !currentStatus
            });
            toast.success(`Utilisateur ${currentStatus ? 'suspendu' : 'réactivé'} avec succès`);
            fetchUsers(currentPage, itemsPerPage, searchTerm);
        } catch (error) {
            console.error('Erreur:', error);
            toast.error("Erreur lors de la modification du statut");
        }
    };

    const renderProfileType = (user) => {
        if (user.is_superuser) {
            return (
                <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded-full text-xs font-medium">
                    Administrateur
                </span>
            );
        } else {
            return (
                <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs font-medium">
                    Demandeur d'emploi
                </span>
            );
        }
    };

    const handleSuspendUser = async (user) => {
        setUserToSuspend(user);
        setShowSuspendModal(true);
    };

    const confirmSuspension = async () => {
        if (!userToSuspend) return;

        try {
            await api.put(`utilisateur/users/${userToSuspend.id}/toggle-status/`, {
                is_active: !userToSuspend.is_active
            });
            toast.success(`Compte ${userToSuspend.is_active ? 'suspendu' : 'réactivé'} avec succès`);
            fetchUsers(currentPage, itemsPerPage, searchTerm);
            setShowSuspendModal(false);
            setUserToSuspend(null);
        } catch (error) {
            console.error('Erreur:', error);
            toast.error("Erreur lors de la modification du statut");
        }
    };

    const handlePasswordChange = async (userId) => {
        try {
            await api.put(`/utilisateur/users/${userId}/change-password/`, passwordForm);
            toast.success('Mot de passe modifié avec succès');
            setShowPasswordModal(false);
            setPasswordForm({ new_password: '', confirm_password: '' });
            setSelectedUserForPassword(null);
        } catch (error) {
            toast.error(error.response?.data?.error || 'Erreur lors de la modification du mot de passe');
        }
    };

    const renderUserProfile = (user) => (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                <div className="p-6 border-b border-gray-200">
                    <div className="flex justify-between items-center">
                        <h3 className="text-lg font-semibold text-gray-800">Profil de l'utilisateur</h3>
                        <button onClick={() => setShowProfileModal(false)} className="text-gray-500 hover:text-gray-700">
                            <X size={24} />
                        </button>
                    </div>
                </div>
                <div className="p-6">
                    <div className="flex items-center space-x-4 mb-6">
                        <div className="h-20 w-20 rounded-full overflow-hidden bg-gray-100">
                            {user.photo_url ? (
                                <img src={user.photo_url} alt="Profile" className="h-full w-full object-cover" />
                            ) : (
                                <div className="h-full w-full flex items-center justify-center bg-orange-100 text-orange-500 text-xl font-semibold">
                                    {user.first_name[0]}{user.last_name[0]}
                                </div>
                            )}
                        </div>
                        <div>
                            <h4 className="text-xl font-semibold">{user.first_name} {user.last_name}</h4>
                            <p className="text-gray-600">{user.email}</p>
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <p className="text-sm font-medium text-gray-500">Nom d'utilisateur</p>
                            <p className="mt-1">{user.username}</p>
                        </div>
                        <div>
                            <p className="text-sm font-medium text-gray-500">Téléphone</p>
                            <p className="mt-1">{user.telephone || '-'}</p>
                        </div>
                        <div>
                            <p className="text-sm font-medium text-gray-500">Date d'inscription</p>
                            <p className="mt-1">{new Date(user.create_at).toLocaleDateString()}</p>
                        </div>
                        <div>
                            <p className="text-sm font-medium text-gray-500">Statut</p>
                            <p className={`mt-1 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${user.is_active
                                    ? 'bg-green-100 text-green-800'
                                    : 'bg-red-100 text-red-800'
                                }`}>
                                {user.is_active ? 'Actif' : 'Suspendu'}
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    const renderTable = () => (
        <div className="overflow-x-auto bg-white rounded-lg shadow">
            <table className="min-w-full">
                <thead className="bg-gray-50">
                    <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Photo</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nom Complet</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                    {currentItems.map((user) => (
                        <tr key={user.id} className="hover:bg-gray-50">
                            {/* Photo cell */}
                            <td className="px-6 py-4 whitespace-nowrap">
                                <div className="flex items-center">
                                    <div className="h-10 w-10 rounded-full overflow-hidden bg-gray-100">
                                        {user.photo_url ? (
                                            <img
                                                src={user.photo_url}
                                                alt={`${user.first_name} ${user.last_name}`}
                                                className="h-full w-full object-cover"
                                            />
                                        ) : (
                                            <div className="h-full w-full flex items-center justify-center bg-orange-100 text-orange-500">
                                                {user.first_name[0]}{user.last_name[0]}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </td>
                            {/* Email cell */}
                            <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-gray-900">{user.email}</div>
                            </td>
                            {/* Nom complet cell */}
                            <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-gray-900">{user.first_name} {user.last_name}</div>
                            </td>
                            {/* Type cell */}
                            <td className="px-6 py-4 whitespace-nowrap">
                                {renderProfileType(user)}
                            </td>
                            {/* Status cell */}
                            <td className="px-6 py-4 whitespace-nowrap">
                                <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${user.is_active
                                        ? 'bg-green-100 text-green-800'
                                        : 'bg-red-100 text-red-800'
                                    }`}>
                                    {user.is_active ? 'Actif' : 'Suspendu'}
                                </span>
                            </td>
                            {/* Actions cell */}
                            <td className="px-6 py-4 whitespace-nowrap">
                                {renderActionButtons(user)}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>

            {/* Pagination */}
            <div className="px-6 py-4 flex items-center justify-between border-t border-gray-200">
                <div className="flex-1 flex justify-between sm:hidden">
                    <button
                        onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                        disabled={currentPage === 1}
                        className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                    >
                        Précédent
                    </button>
                    <button
                        onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                        disabled={currentPage === totalPages}
                        className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                    >
                        Suivant
                    </button>
                </div>
                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                    <div>
                        <p className="text-sm text-gray-700">
                            Affichage de{' '}
                            <span className="font-medium">{indexOfFirstItem + 1}</span>
                            {' à '}
                            <span className="font-medium">
                                {indexOfLastItem}
                            </span>
                            {' sur '}
                            <span className="font-medium">{totalCount}</span>
                            {' résultats'}
                        </p>
                    </div>
                    <div className="flex items-center space-x-4">
                        <select
                            value={itemsPerPage}
                            onChange={(e) => {
                                setItemsPerPage(Number(e.target.value));
                            }}
                            className="border border-gray-300 rounded-md text-sm"
                        >
                            <option value={10}>10 par page</option>
                            <option value={20}>20 par page</option>
                            <option value={50}>50 par page</option>
                        </select>
                        <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                            <button
                                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                disabled={currentPage === 1}
                                className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50"
                            >
                                <ChevronLeft className="h-5 w-5" />
                            </button>
                            {/* Pages */}
                            <div className="flex">
                                {(() => {
                                    const pages = [];
                                    const maxVisible = 5;
                                    let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
                                    let end = Math.min(totalPages, start + maxVisible - 1);
                                    if (end - start + 1 < maxVisible) start = Math.max(1, end - maxVisible + 1);
                                    for (let i = start; i <= end; i++) pages.push(i);
                                    return pages.map(number => (
                                        <button
                                            key={number}
                                            onClick={() => setCurrentPage(number)}
                                            className={`relative inline-flex items-center px-4 py-2 border ${currentPage === number
                                                    ? 'bg-orange-50 border-orange-500 text-orange-600'
                                                    : 'border-gray-300 bg-white text-gray-500 hover:bg-gray-50'
                                                }`}
                                        >
                                            {number}
                                        </button>
                                    ));
                                })()}
                            </div>
                            <button
                                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                disabled={currentPage === totalPages}
                                className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50"
                            >
                                <ChevronRight className="h-5 w-5" />
                            </button>
                        </nav>
                    </div>
                </div>
            </div>
        </div>
    );

    const renderActionButtons = (user) => (
        <div className="flex items-center space-x-3">
            <button
                onClick={() => handleSuspendUser(user)}
                className={`${user.is_active
                        ? 'text-red-600 hover:text-red-800'
                        : 'text-green-600 hover:text-green-800'
                    }`}
                title={user.is_active ? 'Suspendre' : 'Réactiver'}
            >
                {user.is_active ? <Ban size={18} /> : <CheckCircle size={18} />}
            </button>
            <button
                onClick={() => navigate(`/admin/users/${user.id}/edit`)}
                className="text-orange-600 hover:text-orange-800"
                title="Modifier"
            >
                <Edit3 size={18} />
            </button>
            {!user.is_superuser && (
                <button
                    onClick={() => {
                        setSelectedUserForPassword(user);
                        setShowPasswordModal(true);
                    }}
                    className="text-blue-600 hover:text-blue-800"
                    title="Modifier le mot de passe"
                >
                    <Key size={18} />
                </button>
            )}
        </div>
    );

    const renderPasswordModal = () => (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
                <div className="p-6 border-b border-gray-200">
                    <div className="flex justify-between items-center">
                        <h3 className="text-lg font-semibold text-gray-800">
                            Modifier le mot de passe
                        </h3>
                        <button onClick={() => setShowPasswordModal(false)} className="text-gray-500 hover:text-gray-700">
                            <X size={24} />
                        </button>
                    </div>
                </div>
                <div className="p-6 space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Nouveau mot de passe <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                            <input
                                type={showPassword ? "text" : "password"}
                                value={passwordForm.new_password}
                                onChange={(e) => setPasswordForm({
                                    ...passwordForm,
                                    new_password: e.target.value
                                })}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg pr-10"
                            />
                            <button
                                type="button"
                                onClick={() => setShowPassword(!showPassword)}
                                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-600 hover:text-gray-800"
                            >
                                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                            </button>
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Confirmer le mot de passe <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                            <input
                                type={showConfirmPassword ? "text" : "password"}
                                value={passwordForm.confirm_password}
                                onChange={(e) => setPasswordForm({
                                    ...passwordForm,
                                    confirm_password: e.target.value
                                })}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg pr-10"
                            />
                            <button
                                type="button"
                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-600 hover:text-gray-800"
                            >
                                {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                            </button>
                        </div>
                    </div>

                    <div className="flex justify-end space-x-3 mt-6">
                        <button
                            onClick={() => setShowPasswordModal(false)}
                            className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                        >
                            Annuler
                        </button>
                        <button
                            onClick={() => handlePasswordChange(selectedUserForPassword.id)}
                            className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700"
                        >
                            Modifier
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );

    return (
        <div className="flex min-h-screen bg-gray-100">
            <Sidebar
                isOpen={isSidebarOpen}
                menuItems={DemandeurMenu()}
                onClose={() => setIsSidebarOpen(false)}
            />

            <div className="flex-1">
                <Header
                    isSidebarOpen={isSidebarOpen}
                    setIsSidebarOpen={setIsSidebarOpen}
                    user={user}
                />

                <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
                    <div className="mb-8">
                        <h1 className="text-2xl font-bold text-gray-900 mb-4">
                            <Users className="inline-block mr-2 mb-1" />
                            Gestion des utilisateurs
                        </h1>
                        <div className="flex justify-between items-center">
                            <div className="relative w-64">
                                <input
                                    type="text"
                                    placeholder="Rechercher..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                                />
                                <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
                            </div>
                        </div>
                    </div>

                    {loading ? (
                        <div className="flex justify-center items-center h-64">
                            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-orange-500"></div>
                        </div>
                    ) : (
                        renderTable()
                    )}
                </main>
            </div>

            {showProfileModal && selectedUser && renderUserProfile(selectedUser)}

            {/* Modal de confirmation de suspension */}
            {showSuspendModal && userToSuspend && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">
                            Confirmer l'action
                        </h3>
                        <p className="text-gray-600 mb-6">
                            Êtes-vous sûr de vouloir {userToSuspend.is_active ? 'suspendre' : 'réactiver'} le compte de {userToSuspend.first_name} {userToSuspend.last_name} ?
                        </p>
                        <div className="flex justify-end space-x-3">
                            <button
                                onClick={() => {
                                    setShowSuspendModal(false);
                                    setUserToSuspend(null);
                                }}
                                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                            >
                                Annuler
                            </button>
                            <button
                                onClick={confirmSuspension}
                                className={`px-4 py-2 rounded-lg text-white ${userToSuspend.is_active
                                        ? 'bg-red-600 hover:bg-red-700'
                                        : 'bg-green-600 hover:bg-green-700'
                                    }`}
                            >
                                {userToSuspend.is_active ? 'Suspendre' : 'Réactiver'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal de changement de mot de passe */}
            {showPasswordModal && renderPasswordModal()}
        </div>
    );
};

export default UtilisateurPage;
